import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaUser,
  FaPhoneAlt,
  FaEnvelope,
  FaMapMarkerAlt,
  FaShieldAlt,
  FaChevronDown,
  FaChevronUp,
  FaCreditCard,
  FaGooglePay,
  FaUniversity,
  FaCheckCircle,
  FaPercentage,
  FaStickyNote,
  FaSuitcase,
  FaSnowflake,
  FaTicketAlt,
  FaExclamationTriangle,
  FaTag,
  FaCopy,
} from "react-icons/fa";
import api from "../api/Api";

export default function BookingPage() {
  const location = useLocation();
  const navigate = useNavigate();

  // --- STATE ---
  const [bookingData, setBookingData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPriceDetails, setShowPriceDetails] = useState(false);

  // Native Alert State
  const [alertConfig, setAlertConfig] = useState({
    show: false,
    message: "",
    type: "warning",
  });

  // User Form
  const [formData, setFormData] = useState({
    fullName: "",
    phone: "",
    email: "",
    address: "",
    dropAddress: "",
    notes: "",
  });

  // Payment Logic State
  const [paymentMode, setPaymentMode] = useState("partial");
  const [gatewayMethod, setGatewayMethod] = useState("upi");
  const [upiId, setUpiId] = useState("");
  const [selectedBank, setSelectedBank] = useState("");

  // Coupon State
  const [couponCode, setCouponCode] = useState("");
  const [discount, setDiscount] = useState(0);
  const [couponMessage, setCouponMessage] = useState("");

  // Custom Double-Tap Tracker for Mobile
  const [lastTapTime, setLastTapTime] = useState(0);

  useEffect(() => {
    if (!location.state || !location.state.car) {
      navigate("/dashboard");
    } else {
      setBookingData(location.state);

      if (location.state.bookingType?.mode) {
        setPaymentMode(location.state.bookingType.mode);
      }

      const details = location.state.searchDetails;
      setFormData((prev) => ({
        ...prev,
        address: details?.pickupAddress || details?.pickCityName || "",
        dropAddress: details?.dropAddress || "",
      }));
    }
    window.scrollTo(0, 0);
  }, [location, navigate]);
  // Add this useEffect after your existing useEffects
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const token = localStorage.getItem("customerToken");
        if (!token) return;

        const res = await api.get("/customer/profile", {
          headers: { Authorization: `Bearer ${token}` },
        });

        const profile = res.data;

        setFormData((prev) => ({
          ...prev,
          fullName: profile.fullName || "",
          phone: profile.phone || "",
          email: profile.email || "",
        }));
      } catch (err) {
        console.error("Profile fetch failed:", err);
      }
    };

    fetchProfile();
  }, []);

  if (!bookingData) return null;

  const { car, searchDetails, bookingType } = bookingData;
  // console.log("=== FULL FETCHED CAR DATA ===", car);
  // console.log("=== SEARCH DETAILS ===", searchDetails);
  // console.log("=== BOOKING TYPE ===", bookingType);

 const originalCost = bookingType.originalCost;

  // 1. Fixed Auto Discount (Read it directly from Tab 1, DO NOT calculate it here)
  const autoDiscount = bookingType.discountAmount || 0;

  // ==========================================
  // 🧮 DYNAMIC CART MATH (Exactly Matches PHP)
  // ==========================================
  
  // 1. Base Fare after the 1% Auto-Discount (Already calculated in Node)
  const baseFareAfterAutoDiscount = car.basePrice || 0; 

  // 2. Subtract the Promo Code (Coupon) from the Base Fare FIRST
  const afterPromoBaseFare = Math.max(0, baseFareAfterAutoDiscount - discount); 

  // 3. Calculate the Subtotal (Base + Driver Allowance + Night Charge)
  const driverAllowance = car.driverAllowance || 0; 
  const nightCharge = car.nightCharge || 0;
  // If it's a short trip that triggered extra charges, include them
  const extraKmCharge = car.extraKmCharge || 0; 
  
  const subTotal = afterPromoBaseFare + extraKmCharge + driverAllowance + nightCharge; 

  // 4. Calculate dynamic GST on the new lower subtotal
  const gstPercentage = car.gst || 5;
  const dynamicGstAmount = Math.round(subTotal * (gstPercentage / 100)); 

  // 5. Total Price including GST (Before Full Pay Discount)
  const totalPriceWithGst = subTotal + dynamicGstAmount; 

  // 6. Calculate 3% Full Payment Discount on the new Total Price
  const fullPayDiscount = paymentMode === "full" ? Math.round(totalPriceWithGst * 0.03) : 0; 

  // 7. Final Payable Amount
  const finalTotalFare = Math.max(0, totalPriceWithGst - fullPayDiscount); 

  // 8. Advance Payment Split
  const amountToPayNow = paymentMode === "partial" ? Math.round(finalTotalFare * 0.2) : finalTotalFare;
  const amountToPayDriver = paymentMode === "partial" ? finalTotalFare - amountToPayNow : 0;

  // const handleApplyCoupon = () => {
  //   if (couponCode.toUpperCase() === "PATRA500") {
  //     if (paymentMode === "partial") {
  //       const advanceAfterDiscount = Math.round(
  //         (originalCost - autoDiscount) * 0.2,
  //       );
  //       if (originalCost <= 1000) {
  //         setCouponMessage({
  //           type: "error",
  //           text: "Booking amount too low for this coupon.",
  //         });
  //         return;
  //       }
  //       if (advanceAfterDiscount < 500) {
  //         setCouponMessage({
  //           type: "error",
  //           text: "Advance amount is less than ₹500. Use Full Payment for this coupon.",
  //         });
  //         return;
  //       }
  //       setDiscount(500);
  //       setCouponMessage({
  //         type: "success",
  //         text: "Coupon Applied! ₹500 saved.",
  //       });
  //     } else {
  //       if (originalCost > 1000) {
  //         setDiscount(500);
  //         setCouponMessage({
  //           type: "success",
  //           text: "Coupon Applied! ₹500 saved.",
  //         });
  //       } else {
  //         setCouponMessage({
  //           type: "error",
  //           text: "Booking amount too low for this coupon.",
  //         });
  //       }
  //     }
  //   } else {
  //     setDiscount(0);
  //     setCouponMessage({ type: "error", text: "Invalid Coupon Code" });
  //   }
  // };
  const handleApplyCoupon = async () => {
    if (!couponCode) return;

    try {
      // Send the request to your new backend endpoint
      const payload = {
        couponCode: couponCode.toUpperCase(),
        cityId: searchDetails.pickCityId,
        serviceTypeId: searchDetails.serviceTypeId,
        travelDate: searchDetails.date,
        baseFare: car.basePrice || 0,
      };

      const response = await api.post("/carRoutes/validate-promo", payload);

      if (response.data.success) {
        const calculatedDiscount = response.data.discountAmount;

        // Validation: Ensure the 20% advance covers the discount amount
        if (paymentMode === "partial") {
          const advanceAfterAuto = Math.round(
            (originalCost - autoDiscount) * 0.2,
          );
          if (advanceAfterAuto < calculatedDiscount) {
            setCouponMessage({
              type: "error",
              text: `Advance amount is less than ₹${calculatedDiscount}. Switch to Full Payment to use this coupon.`,
            });
            triggerAlert(
              `Advance amount is less than ₹${calculatedDiscount}. Switch to Full Payment to use this coupon.`,
              "warning",
            );
            return;
          }
        }

        // Apply the database-calculated discount
        setDiscount(calculatedDiscount);
        setCouponMessage({ type: "success", text: response.data.message });
      }
    } catch (error) {
      // Handle the error sent by the backend (e.g., Invalid code)
      setDiscount(0);
      const errorMsg = error.response?.data?.message || "Invalid Coupon Code";
      setCouponMessage({ type: "error", text: errorMsg });
    }
  };

  // --- MOBILE FRIENDLY DOUBLE TAP LOGIC ---
  const handleMobileDoubleTap = () => {
    const now = Date.now();
    const timeSinceLastTap = now - lastTapTime;

    if (timeSinceLastTap < 350 && timeSinceLastTap > 0) {
      const codeToApply = "PATRA500";
      setCouponCode(codeToApply);
      if (navigator.clipboard) {
        navigator.clipboard.writeText(codeToApply).catch(() => {});
      }

      // We must call the API here too, so we wait 50ms for state to update, then apply
      setTimeout(() => {
        // Trigger the button click programmatically, or you can extract the logic above into a shared function
        document.getElementById("apply-coupon-btn").click();
      }, 50);
    }

    setLastTapTime(now);
  };

  
  const triggerAlert = (msg, type = "warning") => {
    setAlertConfig({ show: true, message: msg, type: type });
  };

  const handlePayment = async () => {
    const { activeTab } = searchDetails;
    const needsDropAddress = activeTab === "oneway" || activeTab === "round";

    if (!formData.fullName || !formData.phone || !formData.address) {
      triggerAlert("Please fill in Name, Phone, and Pickup Address.");
      return;
    }
    if (!/^\d{10}$/.test(formData.phone)) {
      triggerAlert("Please enter a valid 10-digit phone number");
      return;
    }
    if (needsDropAddress && !formData.dropAddress) {
      triggerAlert("Please provide a Drop Address for this trip.");
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem("token");

      // payload in handlePayment
      const payload = {
        bookingData: {
          pickCityId: searchDetails.pickCityId,
          serviceTypeId: searchDetails.serviceTypeId,
          date: searchDetails.date,
          time: searchDetails.timeId,
          returnDate: searchDetails.returnDate || null,
          returnTimeId: searchDetails.returnTimeId || null,

          fullName: formData.fullName,
          mobile: formData.phone,
          email: formData.email,
          pickupAddress: formData.address,
          dropAddress: formData.dropAddress,
          notes: formData.notes,
          appliedCoupon: discount > 0 ? couponCode : null,
          couponDiscountAmt: discount,
        },
        durationName: (() => {
          const {
            activeTab,
            durationName,
            durationId,
            durationHours,
            taxipkg,
            taxiId,
          } = searchDetails;
          if (activeTab === "local")
            return {
              type: "local",
              label: durationName,
              id: durationId,
              hours: durationHours,
            };
          if (activeTab === "taxi")
            return { type: "taxi", package: taxipkg, id: taxiId };
          if (activeTab === "round")
            return {
              type: "round",
              label: "Round Trip",
              days: searchDetails.totalDays || 1,
            };
          if (activeTab === "oneway")
            return { type: "oneway", label: "One Way" };
          return { type: activeTab };
        })(),
        cab: {
          carId: car.carId || car.id,
          carName: car.carName || car.name,
          baseFare: car.basePrice,
          finalPrice: finalTotalFare,
          tariffId: car.tariffId || null,
          nightCharge: car.nightCharge || 0,
          totalDistance: car.kmIncluded || 0,
          driverAllowance: car.driverAllowance || 0,
          totalSavings: autoDiscount + fullPayDiscount + discount,
          gstPercent: car.gst,
          gstAmount: dynamicGstAmount,
        },
        paymentType: paymentMode === "full" ? 1 : 2,
        payableAmount: amountToPayNow,

        advPercent: paymentMode === "full" ? 100 : 20,
      };

      const config = token
        ? { headers: { Authorization: `Bearer ${token}` } }
        : {};

      // console.log(
      //   "PAYLOAD BEING SENT TO BACKEND:",
      //   JSON.stringify(payload, null, 2),
      // );
      const response = await api.post("/bookingRoutes/create", payload, config);

      if (response?.data?.success) {
        if (
          amountToPayNow > 0 &&
          response.data.payuUrl &&
          response.data.paymentData
        ) {
          const { payuUrl, paymentData } = response.data;
          const form = document.createElement("form");
          form.method = "POST";
          form.action = payuUrl;

          for (let key in paymentData) {
            const input = document.createElement("input");
            input.type = "hidden";
            input.name = key;
            input.value = paymentData[key];
            form.appendChild(input);
          }

          document.body.appendChild(form);
          form.submit();
        } else {
          navigate("/booking-success", {
            state: {
              car,
              searchDetails,
              customer: formData,
              tripDetails: response.data.trip_info,
              paymentInfo: {
                totalFare: finalTotalFare,
                paidAmount: amountToPayNow,
                balanceAmount: amountToPayDriver,
                paymentMode:
                  paymentMode === "partial" ? "Partial (20%)" : "Full Payment",
              },
              bookingId: response.data.enqref_no,
              confirmationId: response.data.confirmation_id,
              txnid: response.data.txnid,
            },
          });
        }
      }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message ||
        "Something went wrong while confirming your ride. Please try again.";
      triggerAlert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="d-flex flex-column min-vh-100 bg-white font-sans">
      {/* NATIVE STYLE ANDROID ALERT */}
      {alertConfig.show && (
        <div className="android-alert-overlay">
          <div className="android-alert-card animate-pop-in">
            <div className="p-4">
              <h5
                className={`fw-bold mb-3 d-flex align-items-center gap-2 ${alertConfig.type === "success" ? "text-success" : "text-dark"}`}
              >
                {alertConfig.type === "success" ? (
                  <FaCheckCircle className="text-success" />
                ) : (
                  <FaExclamationTriangle className="text-warning" />
                )}
                {alertConfig.type === "success" ? "Success" : "Alert"}
              </h5>
              <p className="text-muted mb-4">{alertConfig.message}</p>
              <div className="text-end">
                <button
                  className={`btn fw-bold ${alertConfig.type === "success" ? "text-success" : "text-primary"}`}
                  onClick={() =>
                    setAlertConfig({
                      show: false,
                      message: "",
                      type: "warning",
                    })
                  }
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div
        className="container-fluid px-3 pt-3 flex-grow-1"
        style={{ maxWidth: "800px", margin: "0 auto", paddingBottom: "100px" }}
      >
        {/* TRIP SUMMARY */}
        <div className="card border-0 shadow-sm rounded-4 mb-3 overflow-hidden">
          <div className="card-body p-3">
            <div className="d-flex align-items-center mb-3">
              <div
                className="bg-light rounded-3 p-2 me-3 border d-flex align-items-center justify-content-center"
                style={{ width: "85px", height: "65px" }}
              >
                <img
                  src={car.image}
                  alt={car.name}
                  className="img-fluid"
                  style={{ objectFit: "contain" }}
                />
              </div>
              <div className="flex-grow-1">
                <h6 className="fw-bold m-0 text-capitalize text-dark">
                  {car.name}
                </h6>
                <div className="d-flex gap-1 mt-1 flex-wrap">
                  <span
                    className="badge bg-warning text-dark px-2 py-1"
                    style={{ fontSize: "0.65rem" }}
                  >
                    {searchDetails.activeTab === "round"
                      ? "Round Trip"
                      : searchDetails.activeTab}
                  </span>
                  {searchDetails.activeTab === "local" &&
                    searchDetails.durationName && (
                      <span
                        className="badge bg-primary text-white px-2 py-1"
                        style={{ fontSize: "0.65rem" }}
                      >
                        {searchDetails.durationName}
                      </span>
                    )}
                  {searchDetails.activeTab === "taxi" &&
                    searchDetails.taxipkg && (
                      <span
                        className="badge bg-success text-white px-2 py-1"
                        style={{ fontSize: "0.65rem" }}
                      >
                        {searchDetails.taxipkg}
                      </span>
                    )}
                </div>
              </div>
            </div>

            <div className="d-flex justify-content-between align-items-center bg-light rounded px-3 py-2 mb-3 border small text-dark">
              <span className="fw-semibold">
                <FaUser className="me-1 text-muted" /> {car.seats} Seats
              </span>
              <span className="fw-semibold">
                <FaSuitcase className="me-1 text-muted" /> {car.luggage || 2}{" "}
                Bags
              </span>
              <span className="fw-semibold">
                <FaSnowflake className="text-info me-1" />
                {car.ac === "Yes" ? "AC" : "Non-AC"}
              </span>
            </div>

            <div className="d-flex align-items-start gap-2 mb-2">
              <div className="d-flex flex-column align-items-center mt-1">
                <div
                  className="bg-success rounded-circle"
                  style={{ width: "8px", height: "8px" }}
                ></div>
                {(searchDetails.activeTab === "oneway" ||
                  searchDetails.activeTab === "round") && (
                  <>
                    <div
                      className="vr"
                      style={{ height: "20px", opacity: 0.3 }}
                    ></div>
                    <div
                      className="bg-danger rounded-circle"
                      style={{ width: "8px", height: "8px" }}
                    ></div>
                  </>
                )}
              </div>
              <div className="flex-grow-1 small">
                <div className="fw-bold text-dark">
                  {searchDetails.pickupAddress || searchDetails.pickCityName}
                </div>
                {(searchDetails.activeTab === "oneway" ||
                  searchDetails.activeTab === "round") && (
                  <div className="fw-bold mt-2 text-muted">
                    {searchDetails.dropAddress}
                  </div>
                )}
              </div>
              <div className="text-end small text-muted">
                <div className="fw-semibold">
                  {new Date(searchDetails.date).toLocaleDateString("en-GB", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })}
                </div>

                <div>{searchDetails.timeName}</div>

                {searchDetails.activeTab === "round" &&
                  searchDetails.returnDate && (
                    <div className="mt-2">
                      <div className="fw-semibold text-danger">
                        Ret:{" "}
                        {new Date(searchDetails.returnDate).toLocaleDateString(
                          "en-GB",
                          { day: "numeric", month: "short", year: "numeric" },
                        )}
                      </div>
                      {/* FIX: Use returnTimeName instead of returnTimeId for the UI */}
                      <div className="text-danger">
                        {searchDetails.returnTimeName || "Selected Time"}
                      </div>
                    </div>
                  )}
              </div>
            </div>
          </div>

          <div
            className="bg-light border-top p-3 d-flex justify-content-between align-items-center cursor-pointer"
            onClick={() => setShowPriceDetails(!showPriceDetails)}
          >
            <span className="small fw-bold text-secondary">Total Fare</span>
            <div className="d-flex align-items-center gap-2">
              <span className="fw-bold text-dark">
                ₹{finalTotalFare.toLocaleString()}
              </span>
              {showPriceDetails ? (
                <FaChevronUp size={12} className="text-muted" />
              ) : (
                <FaChevronDown size={12} className="text-muted" />
              )}
            </div>
          </div>

          {showPriceDetails && (
            <div className="p-3 border-top small animate-slide-down bg-white">
              <div className="d-flex justify-content-between mb-1 text-muted">
                <span>Rental Price</span>
                <span>₹{(car.basePrice || 0).toLocaleString()}</span>
              </div>

              {car.driverAllowance > 0 && (
                <div className="d-flex justify-content-between mb-1 text-muted">
                  <span>Driver Allowance</span>
                  <span>₹{(car.driverAllowance || 0).toLocaleString()}</span>
                </div>
              )}

              {car.gst > 0 && (
                <div className="d-flex justify-content-between mb-1 text-muted">
                  <span>GST ({car.gst || 0}%)</span>
                  <span>₹{dynamicGstAmount.toLocaleString()}</span>
                </div>
              )}

              {car.nightCharge > 0 && (
                <div className="d-flex justify-content-between mb-1 text-muted">
                  <span>Night Charge</span>
                  <span>₹{(car.nightCharge || 0).toLocaleString()}</span>
                </div>
              )}

              <div className="d-flex justify-content-between mb-1 text-success fw-bold">
                <span>
                  <FaTag className="me-1" /> Special Discount (1%)
                </span>
                <span>-₹{autoDiscount.toLocaleString()}</span>
              </div>

              {fullPayDiscount > 0 && (
                <div className="d-flex justify-content-between mb-1 text-success fw-bold">
                  <span>
                    <FaTag className="me-1" /> Full Pay Discount (3%)
                  </span>
                  <span>-₹{fullPayDiscount.toLocaleString()}</span>
                </div>
              )}

              {discount > 0 && (
                <div className="d-flex justify-content-between mb-1 text-success fw-bold">
                  <span>
                    <FaTicketAlt className="me-1" /> Coupon Discount
                  </span>
                  <span>-₹{discount.toLocaleString()}</span>
                </div>
              )}

              <div className="mt-2 text-danger" style={{ fontSize: "0.75rem" }}>
                * Toll, State Tax & Parking charges are extra as applicable.
              </div>

              <div className="border-top my-2"></div>
              <div className="d-flex justify-content-between fw-bold fs-6 text-dark">
                <span>Final Total</span>
                <span className="text-success">
                  ₹{finalTotalFare.toLocaleString()}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* COUPON SECTION */}
        <div className="card border-0 shadow-sm rounded-4 mb-3">
          <div className="card-body p-3">
            <label className="fw-bold text-secondary small mb-2 d-flex align-items-center gap-1">
              <FaTicketAlt className="text-warning" /> APPLY COUPON
            </label>

            {!discount && (
              <div
                className="bg-primary bg-opacity-10 border border-primary border-opacity-25 rounded-3 p-2 mb-3 text-center cursor-pointer transition-all hover-shadow"
                onClick={handleMobileDoubleTap}
                style={{ userSelect: "none", touchAction: "manipulation" }}
              >
                <small className="text-primary fw-bold d-flex align-items-center justify-content-center gap-2">
                  <FaCopy /> Apply this Coupon:
                  <span className="fs-6 bg-white px-2 py-1 rounded border border-primary border-opacity-50 text-dark">
                    PATRA500
                  </span>
                </small>
                <div
                  style={{ fontSize: "0.65rem" }}
                  className="text-muted mt-1 fw-semibold"
                >
                  Save Up to ₹500 on trips above ₹1000
                </div>
              </div>
            )}

            <div className="input-group">
              <input
                type="text"
                className="form-control bg-light border-0 fw-bold text-uppercase"
                placeholder="Enter Code"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                disabled={discount > 0}
              />
              {discount > 0 ? (
                <button
                  className="btn btn-danger px-4 fw-bold"
                  onClick={() => {
                    setDiscount(0);
                    setCouponCode("");
                    setCouponMessage("");
                  }}
                >
                  Remove
                </button>
              ) : (
                <button
                  id="apply-coupon-btn"
                  className="btn btn-dark px-4 fw-bold"
                  onClick={handleApplyCoupon}
                >
                  Apply
                </button>
              )}
            </div>
            {couponMessage && (
              <small
                className={`d-block mt-2 fw-semibold ${couponMessage.type === "success" ? "text-success" : "text-danger"}`}
              >
                {couponMessage.text}
              </small>
            )}
          </div>
        </div>

        {/* CUSTOMER FORM */}
        <h6 className="ms-1 mt-4 mb-2 small fw-bold text-secondary text-uppercase">
          Customer Details
        </h6>
        <div className="card border border-secondary border-opacity-25 shadow-sm rounded-4 mb-3">
          <div className="card-body p-3">
            <div className="mb-3 position-relative">
              <FaUser
                className="position-absolute text-muted"
                style={{ top: "12px", left: "12px" }}
              />
              <input
                className="form-control bg-light border border-secondary border-opacity-25 ps-5 py-2"
                placeholder="Full Name"
                value={formData.fullName}
                onChange={(e) =>
                  setFormData({ ...formData, fullName: e.target.value })
                }
                readOnly
              />
            </div>
            <div className="mb-3 position-relative">
              <FaPhoneAlt
                className="position-absolute text-muted"
                style={{ top: "12px", left: "12px" }}
              />
              <input
                type="tel"
                className="form-control bg-light border border-secondary border-opacity-25 ps-5 py-2"
                placeholder="Phone Number"
                maxLength={10}
                value={formData.phone}
                onChange={(e) => {
                  const onlyNums = e.target.value.replace(/\D/g, "");
                  setFormData({ ...formData, phone: onlyNums });
                }}
                readOnly
              />
            </div>
            <div className="mb-3 position-relative">
              <FaEnvelope
                className="position-absolute text-muted"
                style={{ top: "12px", left: "12px" }}
              />
              <input
                type="email"
                className="form-control bg-light border border-secondary border-opacity-25 ps-5 py-2"
                placeholder="Email Address"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                readOnly
              />
            </div>
            <div className="mb-3 position-relative">
              <FaMapMarkerAlt
                className="position-absolute text-success"
                style={{ top: "12px", left: "12px" }}
              />
              <textarea
                className="form-control bg-light border border-secondary border-opacity-25 ps-5 py-2"
                rows="2"
                placeholder="Pickup Address"
                value={formData.address}
                onChange={(e) =>
                  setFormData({ ...formData, address: e.target.value })
                }
              />
            </div>
            <div className="mb-3 position-relative">
              <FaMapMarkerAlt
                className="position-absolute text-danger"
                style={{ top: "12px", left: "12px" }}
              />
              <textarea
                className="form-control bg-light border border-secondary border-opacity-25 ps-5 py-2"
                rows="2"
                placeholder="Drop Address"
                value={formData.dropAddress}
                onChange={(e) =>
                  setFormData({ ...formData, dropAddress: e.target.value })
                }
              />
            </div>
            <div className="position-relative mb-0">
              <FaStickyNote
                className="position-absolute text-muted"
                style={{ top: "12px", left: "12px" }}
              />
              <input
                className="form-control bg-light border border-secondary border-opacity-25 ps-5 py-2"
                placeholder="Any instructions for driver?"
                value={formData.notes}
                onChange={(e) =>
                  setFormData({ ...formData, notes: e.target.value })
                }
              />
            </div>
          </div>
        </div>

        {/* PAYMENT SECTION */}
        <h6 className="ms-1 mt-4 mb-2 small fw-bold text-secondary text-uppercase">
          Payment Option
        </h6>
        <div className="card border-0 shadow-sm rounded-4 mb-4 overflow-hidden">
          <div className="d-flex p-1 bg-light m-2 rounded-3 gap-1">
            <button
              className={`flex-fill btn btn-sm rounded-2 py-2 px-3 fw-bold transition-all
                ${paymentMode === "partial" ? "bg-white shadow-sm text-warning" : "text-muted border-0"}`}
              onClick={() => setPaymentMode("partial")}
            >
              20% Advance
            </button>
            <button
              className={`flex-fill btn btn-sm rounded-2 py-2 px-3 fw-bold transition-all
                ${paymentMode === "full" ? "bg-white shadow-sm text-primary" : "text-muted border-0"}`}
              onClick={() => setPaymentMode("full")}
            >
              Full Pay <span className="badge bg-success ms-1">3% OFF</span>
            </button>
          </div>

          <div className="card-body p-0">
            <div className="p-3 animate-fade-in">
              {paymentMode === "partial" && (
                <div className="p-3 bg-warning bg-opacity-10 rounded-3 mb-3 border border-warning border-opacity-25">
                  <div className="d-flex align-items-center mb-1">
                    <FaPercentage size={14} className="me-2 text-warning" />
                    <span className="fw-bold small text-dark">
                      Secure Booking with 20% Advance
                    </span>
                  </div>
                  <small className="text-muted d-block">
                    Pay <b>₹{amountToPayNow.toLocaleString()}</b> now to confirm
                    your ride. Remaining{" "}
                    <b>₹{amountToPayDriver.toLocaleString()}</b> pay to driver.
                  </small>
                </div>
              )}

              {paymentMode === "full" && (
                <div className="p-3 bg-success bg-opacity-10 rounded-3 mb-3 border border-success border-opacity-25">
                  <div className="d-flex align-items-center mb-1">
                    <FaCheckCircle size={14} className="me-2 text-success" />
                    <span className="fw-bold small text-dark">
                      Full Payment — 3% Extra Discount Applied!
                    </span>
                  </div>
                  <small className="text-muted d-block">
                    Pay full <b>₹{amountToPayNow.toLocaleString()}</b> and save
                    extra <b>₹{fullPayDiscount.toLocaleString()}</b>.
                  </small>
                </div>
              )}

              {[
                {
                  id: "upi",
                  label: "UPI (GPay, PhonePe)",
                  icon: <FaGooglePay size={26} className="text-dark" />,
                },
                {
                  id: "card",
                  label: "Debit/Credit Card",
                  icon: <FaCreditCard size={20} className="text-secondary" />,
                },
                {
                  id: "netbanking",
                  label: "Net Banking",
                  icon: <FaUniversity size={20} className="text-secondary" />,
                },
              ].map((m) => (
                <div
                  key={m.id}
                  onClick={() => setGatewayMethod(m.id)}
                  className={`d-flex align-items-center p-3 mb-2 rounded-4 cursor-pointer border transition-all
                        ${gatewayMethod === m.id ? "border-primary bg-primary bg-opacity-10" : "border-light"}`}
                >
                  <div
                    className={`rounded-circle me-3 d-flex align-items-center justify-content-center
                            ${gatewayMethod === m.id ? "bg-primary" : "border border-2"}`}
                    style={{ width: 20, height: 20 }}
                  >
                    {gatewayMethod === m.id && (
                      <FaCheckCircle className="text-white" size={12} />
                    )}
                  </div>
                  <div className="me-3">{m.icon}</div>
                  <span
                    className={`flex-grow-1 fw-semibold small ${gatewayMethod === m.id ? "text-primary" : "text-dark"}`}
                  >
                    {m.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* STICKY BOTTOM BAR */}
      <div
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          background: "#fff",
          borderTop: "1px solid #f1f5f9",
          boxShadow: "0 -4px 20px rgba(0,0,0,0.08)",
          padding: "16px",
          zIndex: 100,
        }}
      >
        <div style={{ maxWidth: "600px", margin: "0 auto" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "35px",
            }}
          >
            <div>
              <div
                style={{
                  fontSize: "10px",
                  fontWeight: "700",
                  color: "#94a3b8",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Pay Now
              </div>
              <div
                style={{
                  fontSize: "24px",
                  fontWeight: "800",
                  color: "#0f172a",
                  lineHeight: "1.1",
                }}
              >
                ₹{amountToPayNow.toLocaleString()}
              </div>
              {amountToPayDriver > 0 && (
                <div
                  style={{
                    fontSize: "11px",
                    color: "#ef4444",
                    fontWeight: "600",
                    marginTop: "2px",
                  }}
                >
                  + ₹{amountToPayDriver.toLocaleString()} to driver later
                </div>
              )}
            </div>
            <button
              onClick={handlePayment}
              disabled={loading}
              style={{
                background: loading
                  ? "#94a3b8"
                  : "linear-gradient(135deg, #f97316, #fb923c)",
                color: "#fff",
                border: "none",
                borderRadius: "16px",
                padding: "16px 28px",
                fontWeight: "800",
                fontSize: "15px",
                cursor: loading ? "not-allowed" : "pointer",
                fontFamily: "inherit",
                minWidth: "160px",
                boxShadow: loading ? "none" : "0 4px 16px rgba(249,115,22,0.4)",
                transition: "all 0.2s",
              }}
            >
              {loading
                ? "Processing…"
                : `Pay ₹${amountToPayNow.toLocaleString()}`}
            </button>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "6px",
              fontSize: "11px",
              color: "#94a3b8",
            }}
          >
            <FaShieldAlt style={{ color: "#22c55e" }} />
            <span>100% Safe & Secure · Powered by PayU</span>
          </div>
        </div>
      </div>

      {/* STYLES */}
      <style>{`
                @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
                
                .android-alert-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    width: 100vw;
                    height: 100%;
                    background: rgba(0, 0, 0, 0.4);
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    z-index: 9999;
                }

                .android-alert-card {
                    background: white;
                    border-radius: 16px;
                    width: 90%;
                    max-width: 350px;
                    box-shadow: 0 10px 30px rgba(0,0,0,0.2);
                }

                .animate-pop-in { 
                    animation: popIn 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards; 
                }

                @keyframes popIn { 
                    from { transform: scale(0.85); opacity: 0; } 
                    to { transform: scale(1); opacity: 1; } 
                }

                * { -webkit-tap-highlight-color: transparent; }
                input::placeholder, textarea::placeholder { color: #cbd5e1; }
            `}</style>
    </div>
  );
}
